import { useEffect, useRef, useState } from "react";
import { MoreHorizontal, X } from "lucide-react";
import { Button } from "../ui/Button.js";
import { CopyItemsButton } from "../ui/CopyItemsButton.js";

export function SelectionActionBar({
  visible,
  count,
  canWrite,
  canSaveList,
  busy,
  selectedItems,
  onMove,
  onSaveAsList,
  onCheck,
  onDelete,
  onExitMode,
}: {
  visible: boolean;
  count: number;
  canWrite: boolean;
  canSaveList: boolean;
  busy: boolean;
  selectedItems: readonly { text: string }[];
  onMove: () => void;
  onSaveAsList: () => void;
  onCheck: (checked: boolean) => void;
  onDelete: () => void;
  onExitMode: () => void;
}) {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!overflowOpen) return;
    function onPointerDown(e: PointerEvent) {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node)) {
        setOverflowOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [overflowOpen]);

  if (!visible) return null;

  const showOverflow = canWrite || canSaveList;
  const noSelection = count === 0;

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-ink text-white pb-[env(safe-area-inset-bottom)]">
      <div className="mx-auto flex max-w-2xl flex-wrap items-center gap-2 px-4 py-3">
        <span className="text-sm font-semibold tabular-nums">
          {count === 0 ? "Select items" : `${count} selected`}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canWrite ? (
            <Button type="button" size="sm" disabled={busy || noSelection} onClick={onMove}>
              Move to…
            </Button>
          ) : null}
          {canSaveList ? (
            <Button
              type="button"
              variant="inverted"
              size="sm"
              disabled={busy || noSelection}
              className="hidden sm:inline-flex"
              onClick={onSaveAsList}
            >
              Save as list
            </Button>
          ) : null}
          <CopyItemsButton items={selectedItems} variant="inverted" />
          {showOverflow ? (
            <div
              className="relative"
              ref={overflowRef}
              onKeyDown={(e) => {
                if (e.key === "Escape" && overflowOpen) {
                  // Closing the popover must not also clear the selection (H2-style hazard):
                  // stop the native event before ChecklistPage's document-level Escape
                  // listener (which clears the selection) ever sees it.
                  e.stopPropagation();
                  setOverflowOpen(false);
                }
              }}
            >
              <Button
                type="button"
                variant="inverted"
                size="icon-sm"
                aria-label="More actions"
                title="More actions"
                disabled={busy || noSelection}
                onClick={() => setOverflowOpen((v) => !v)}
                icon={<MoreHorizontal size={18} aria-hidden="true" />}
              />
              {overflowOpen ? (
                <div className="absolute bottom-full right-0 mb-2 w-48 rounded-lg border border-line bg-white p-1 text-ink shadow-lg">
                  {canSaveList ? (
                    <button
                      type="button"
                      className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 sm:hidden"
                      disabled={busy || noSelection}
                      onClick={() => {
                        setOverflowOpen(false);
                        onSaveAsList();
                      }}
                    >
                      Save as list
                    </button>
                  ) : null}
                  {canWrite ? (
                    <>
                      <button
                        type="button"
                        className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={busy || noSelection}
                        onClick={() => {
                          setOverflowOpen(false);
                          onCheck(true);
                        }}
                      >
                        Check selected
                      </button>
                      <button
                        type="button"
                        className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={busy || noSelection}
                        onClick={() => {
                          setOverflowOpen(false);
                          onCheck(false);
                        }}
                      >
                        Uncheck selected
                      </button>
                      <button
                        type="button"
                        className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={busy || noSelection}
                        onClick={() => {
                          setOverflowOpen(false);
                          onDelete();
                        }}
                      >
                        Delete selected
                      </button>
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
          <Button
            type="button"
            variant="inverted"
            size="icon-sm"
            aria-label="Exit multi-select"
            title="Exit multi-select"
            onClick={onExitMode}
            icon={<X size={18} aria-hidden="true" />}
          />
        </div>
      </div>
    </div>
  );
}
